import React, {useCallback, useEffect, useState} from 'react'
import Cropper from 'react-easy-crop'
import CircularProgress from '@mui/material/CircularProgress'

import {BaseModal} from '../../components/BaseModal'
import {useThemeStore} from '../../stores/themeStore'
import {getCroppedLogoBlob} from './cropLogoImage'

// Crop + zoom dialog for a freshly-selected logo. It does not upload — it returns
// a normalized Blob to the parent via onSave, which owns the request. The preview
// of how the logo looks on worksheets / copy-paste is intentionally out of scope.
const ASPECTS = [
    {label: 'Wide', value: 3},
    {label: 'Standard', value: 2},
    {label: 'Square', value: 1},
]

const MAX_ZOOM = 3

// react-easy-crop's zoom=1 makes the media *cover* the crop frame (the frame is
// the largest aspect-rect that fits inside the media), so wide/tall logos start
// clipped. The zoom at which the whole media instead fits *inside* the frame is
// the ratio of the smaller to the larger aspect — used as both the starting zoom
// and the minimum, so the default view always shows the complete logo and the
// user can only zoom in from there.
const fitZoom = (mediaAspect, cropAspect) =>
    Math.min(mediaAspect, cropAspect) / Math.max(mediaAspect, cropAspect)

export const LogoCropDialog = ({imageSrc, isSaving, onCancel, onSave}) => {
    const theme = useThemeStore((state) => state.theme)
    const [crop, setCrop] = useState({x: 0, y: 0})
    const [zoom, setZoom] = useState(1)
    const [minZoom, setMinZoom] = useState(1)
    const [aspect, setAspect] = useState(3)
    const [mediaAspect, setMediaAspect] = useState(null)
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
    const [error, setError] = useState(null)

    const onCropComplete = useCallback((_area, areaPixels) => {
        setCroppedAreaPixels(areaPixels)
    }, [])

    const onMediaLoaded = useCallback((mediaSize) => {
        setMediaAspect(mediaSize.naturalWidth / mediaSize.naturalHeight)
    }, [])

    // Re-fit whenever the image loads or the chosen aspect changes: the whole logo
    // is contained in the frame (zoom = fit), never clipped. Zoom-in from here is
    // still allowed; zoom-out below full-fit is not (minZoom = fit).
    useEffect(() => {
        if (!mediaAspect) return
        const fit = fitZoom(mediaAspect, aspect)
        setMinZoom(fit)
        setZoom(fit)
        setCrop({x: 0, y: 0})
    }, [mediaAspect, aspect])

    const handleSave = async () => {
        if (!croppedAreaPixels) return
        setError(null)
        try {
            const blob = await getCroppedLogoBlob(imageSrc, croppedAreaPixels)
            onSave(blob)
        } catch (e) {
            setError('Could not process the image. Please try another.')
        }
    }

    const wrapperStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        padding: 0,
        width: 'min(92vw, 560px)',
    }

    return (
        <BaseModal isOpen setIsOpen={() => onCancel()} style={wrapperStyle}>
            <div className={'LogoCropDialog'} data-theme={theme} role={'dialog'} aria-label={'Crop your logo'}>
                <div className={'LogoCropDialog__header'}>
                    <span className={'LogoCropDialog__title'}>Crop Your Logo</span>
                    <button
                        type={'button'}
                        className={'LogoCropDialog__close'}
                        aria-label={'Close'}
                        onClick={() => onCancel()}
                    >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="6" y1="6" x2="18" y2="18"/>
                            <line x1="18" y1="6" x2="6" y2="18"/>
                        </svg>
                    </button>
                </div>

                <div className={'LogoCropDialog__stage'}>
                    <Cropper
                        image={imageSrc}
                        crop={crop}
                        zoom={zoom}
                        minZoom={minZoom}
                        maxZoom={MAX_ZOOM}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                        onMediaLoaded={onMediaLoaded}
                        restrictPosition={false}
                    />
                </div>

                <div className={'LogoCropDialog__controls'}>
                    <div className={'LogoCropDialog__aspects'} role={'group'} aria-label={'Aspect ratio'}>
                        {ASPECTS.map((a) => (
                            <button
                                key={a.value}
                                type={'button'}
                                className={'LogoCropDialog__aspect' + (aspect === a.value ? ' active' : '')}
                                onClick={() => setAspect(a.value)}
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>

                    <label className={'LogoCropDialog__zoom'}>
                        <span>Zoom</span>
                        <input
                            type={'range'}
                            min={minZoom}
                            max={MAX_ZOOM}
                            step={0.01}
                            value={zoom}
                            aria-label={'Zoom'}
                            onChange={(e) => setZoom(Number(e.target.value))}
                        />
                    </label>
                </div>

                {error && <div className={'LogoCropDialog__error'} role={'alert'}>{error}</div>}

                <div className={'LogoCropDialog__actions'}>
                    <button
                        type={'button'}
                        className={'LogoCropDialog__btn'}
                        onClick={() => onCancel()}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                    <button
                        type={'button'}
                        className={'LogoCropDialog__btn LogoCropDialog__btn--primary'}
                        aria-label={'Save logo'}
                        onClick={handleSave}
                        disabled={isSaving || !croppedAreaPixels}
                    >
                        {isSaving ? <CircularProgress size={16} color={'inherit'}/> : 'Save Logo'}
                    </button>
                </div>
            </div>
        </BaseModal>
    )
}
